import { AppConfig } from "../../environement";

export interface MusicianCardData {
  firstName?: string;
  middleName?: string;
  lastName?: string;
  nickName?: string;
  companyName?: string;
  email?: string;
  phone?: string;
  avatar?: string;
  instruments?: string[];
}

/**
 * Current environment code, derived the same way the rest of the app derives hosts.
 *
 * @returns {string} "live", "demo" or "beta"
 */
function currentEnv(): string {
  const endpoint = AppConfig?.api?.endpoint ?? "";
  if (endpoint.includes("beta")) return "beta";
  if (endpoint.includes("demo")) return "demo";
  return "live";
}

/**
 * Universal-link host per environment. There is no `mobile.beta` host — the
 * beta API pairs with the `mobile.alpha` deep-link domain.
 *
 * @param {string} env environment code
 * @returns {string} the mobile universal-link host
 */
function mobileHost(env: string): string {
  switch (env) {
    case "demo":
      return "mobile.demo.rhapsody.la";
    case "beta":
      return "mobile.alpha.rhapsody.la";
    default:
      return "mobile.live.rhapsody.la";
  }
}

/**
 * UTF-8 safe base64url (keeps `=` padding, matching the mobile encoder).
 *
 * @param {unknown} value JSON-serializable payload
 * @returns {string} base64url-encoded JSON
 */
function toBase64Url(value: unknown): string {
  const bytes = new TextEncoder().encode(JSON.stringify(value));
  let binary = "";
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_");
}

/**
 * Builds the universal link encoded into the musician's QR code — identical to
 * what the Rhapsody mobile app generates and what the deep-link host +
 * studio-frontend already handle:
 *   https://mobile.{env}.rhapsody.la/secureAccess?action=add&endpoint={code}&card={base64url}
 * where the decoded payload is `{"model":"musician", ...}`.
 *
 * @param {MusicianCardData} card the musician's contact fields
 * @returns {string} the universal link to encode in the QR code
 */
export function buildMusicianCardLink(card: MusicianCardData): string {
  const env = currentEnv();

  const payload: Record<string, unknown> = { model: "musician" };
  const put = (key: string, val?: string) => {
    if (val && val.trim()) payload[key] = val.trim();
  };
  put("firstName", card.firstName);
  put("lastName", card.lastName);
  put("middleName", card.middleName);
  put("nickName", card.nickName);
  put("companyName", card.companyName);
  put("email", card.email);
  put("phone", card.phone);
  put("avatar", card.avatar);
  const instruments = (card.instruments ?? [])
    .map((i) => i.trim())
    .filter(Boolean);
  if (instruments.length) payload.instruments = instruments;

  const params = new URLSearchParams({ action: "add", endpoint: env });
  params.set("card", toBase64Url(payload));
  return `https://${mobileHost(env)}/secureAccess?${params.toString()}`;
}
